import React, { useState, useEffect } from 'react';
import { TrendingUp, Mail, Eye, MousePointer, Clock, DollarSign, AlertTriangle, CheckCircle } from 'lucide-react';
import { commercialService, type LeadScore, type PendingEmail, type EmailLog, type CommercialMetrics } from '../services/commercialService';
import { leadsService } from '../services/supabaseService';
import type { Lead } from '../types';

const classificationConfig: Record<string, { emoji: string; color: string; bg: string }> = {
  PRIORIDAD_ALTA: { emoji: '🔥', color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
  INTERESANTE: { emoji: '🟢', color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  DUDOSO: { emoji: '🟡', color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200' },
  DESCARTAR: { emoji: '🔴', color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200' },
};

const riskLabels: Record<string, string> = {
  RISK_NO_BUDGET: "Sin presupuesto",
  RISK_NO_DECISION_MAKER: "Sin decisor",
  RISK_SCOPE_TOO_BIG: "Alcance excesivo",
  RISK_LONG_SALES_CYCLE: "Ciclo largo",
  RISK_MICROMANAGER: "Microgestión",
  RISK_LOW_BUDGET: "Presupuesto bajo",
  RISK_INSTITUTIONAL_SLOW_PROCESS: "Proceso lento",
  RISK_PARTNERSHIP_NO_REVENUE_MODEL: "Partner sin modelo",
};

function humanizeRisk(flag: string): string {
  return riskLabels[flag] || flag.replace(/^RISK_/, "").replace(/_/g, " ").toLowerCase();
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatEur(v: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}

const CommercialDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<CommercialMetrics | null>(null);
  const [scores, setScores] = useState<LeadScore[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [pendingEmails, setPendingEmails] = useState<PendingEmail[]>([]);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'pipeline' | 'pending' | 'emails'>('pipeline');

  useEffect(() => {
    async function load() {
      try {
        const [m, s, p, e, l] = await Promise.all([
          commercialService.getMetrics(),
          commercialService.getLatestScores(30),
          commercialService.getPendingEmails(),
          commercialService.getEmailLogs(30),
          leadsService.getAll(),
        ]);
        setMetrics(m);
        setScores(s);
        setPendingEmails(p);
        setEmailLogs(e);
        setLeads(l);
      } catch (err) {
        console.error('Error loading commercial data:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!metrics) return <div className="p-8 text-center text-gray-500">Error cargando datos</div>;

  // Deduplicate scores: latest per lead
  const latestScores = new Map<string, LeadScore>();
  for (const s of scores) {
    if (!latestScores.has(s.lead_id)) latestScores.set(s.lead_id, s);
  }

  // Merge with lead data
  const pipeline = Array.from(latestScores.values()).map((score) => {
    const lead = leads.find((l) => l.id === score.lead_id);
    return { ...score, lead };
  }).sort((a, b) => b.score - a.score);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Asistente Comercial IA</h1>
        <p className="text-gray-600">Pipeline inteligente, scoring de leads y gestión de emails comerciales</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <MetricCard icon={TrendingUp} label="Leads (30d)" value={metrics.totalLeads} />
        <MetricCard icon={DollarSign} label="Pipeline" value={formatEur(metrics.pipelineValueEur)} />
        <MetricCard icon={Mail} label="Emails (30d)" value={metrics.emailsSent30d} />
        <MetricCard icon={Eye} label="Open rate" value={`${metrics.openRate}%`} />
        <MetricCard icon={MousePointer} label="Clicks" value={metrics.clicks30d} />
        <MetricCard icon={Clock} label="Pendientes" value={metrics.pendingApproval} highlight={metrics.pendingApproval > 0} />
      </div>

      {/* Pipeline cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(['PRIORIDAD_ALTA', 'INTERESANTE', 'DUDOSO', 'DESCARTAR'] as const).map((cls) => {
          const config = classificationConfig[cls];
          const count = metrics.distribution[cls] || 0;
          const items = pipeline.filter((p) => p.classification === cls);
          return (
            <div key={cls} className={`rounded-lg border p-4 ${config.bg}`}>
              <h3 className={`text-sm font-semibold mb-2 ${config.color}`}>
                {config.emoji} {cls.replace('_', ' ')} ({count})
              </h3>
              <div className="space-y-1">
                {items.slice(0, 4).map((item) => (
                  <div key={item.id} className="text-xs">
                    <span className="font-medium">{item.lead?.full_name || '?'}</span>
                    <span className="text-gray-500 ml-1">{item.score}/100</span>
                  </div>
                ))}
                {items.length > 4 && <div className="text-xs text-gray-400">+{items.length - 4} más</div>}
                {items.length === 0 && <div className="text-xs text-gray-400">Sin leads</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-4">
          {([
            { id: 'pipeline' as const, label: 'Pipeline' },
            { id: 'pending' as const, label: `Pendientes${pendingEmails.length > 0 ? ` (${pendingEmails.length})` : ''}` },
            { id: 'emails' as const, label: 'Emails enviados' },
          ]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-4 text-sm font-medium border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'pipeline' && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Lead</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Empresa</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Clasificación</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Riesgos</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pipeline.map((item) => {
                const config = classificationConfig[item.classification] || classificationConfig.DUDOSO;
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium">{item.lead?.full_name || '?'}</div>
                      <div className="text-xs text-gray-500">{item.lead?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">{item.lead?.company || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-bold">{item.score}</span>
                      <span className="text-xs text-gray-400">/100</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.color}`}>
                        {config.emoji} {item.classification}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {item.risk_flags.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {item.risk_flags.slice(0, 2).map((flag, i) => (
                            <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-orange-50 text-orange-700 border border-orange-200">
                              {humanizeRisk(String(flag))}
                            </span>
                          ))}
                          {item.risk_flags.length > 2 && (
                            <span className="text-xs text-gray-400">+{item.risk_flags.length - 2}</span>
                          )}
                        </div>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(item.created_at)}</td>
                  </tr>
                );
              })}
              {pipeline.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Sin leads analizados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'pending' && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Destinatario</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Asunto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pendingEmails.map((email) => (
                <tr key={email.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{email.recipient}</td>
                  <td className="px-4 py-3 text-sm">{email.subject}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-50 text-blue-700">
                      {email.action_type.replace('email_', '')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{email.score}/100</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                      email.status === 'pending' ? 'bg-yellow-50 text-yellow-700' : 'bg-gray-50 text-gray-600'
                    }`}>
                      {email.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatDate(email.created_at)}</td>
                </tr>
              ))}
              {pendingEmails.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Sin emails pendientes</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'emails' && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Destinatario</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Asunto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Entregado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Abierto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Clicks</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {emailLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{log.recipient}</td>
                  <td className="px-4 py-3 text-sm">{log.subject}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                      log.bounced_at ? 'bg-red-50 text-red-700' :
                      log.send_status === 'sent' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'
                    }`}>
                      {log.bounced_at ? 'bounced' : log.send_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{log.delivered_at ? '✅' : log.send_status === 'sent' ? '⏳' : '—'}</td>
                  <td className="px-4 py-3 text-sm">{log.opened_at ? `👀 ${log.open_count}x` : '—'}</td>
                  <td className="px-4 py-3 text-sm">{log.click_count > 0 ? `🔗 ${log.click_count}x` : '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatDate(log.created_at)}</td>
                </tr>
              ))}
              {emailLogs.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Sin emails enviados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Metric card component
function MetricCard({ icon: Icon, label, value, highlight }: {
  icon: React.FC<any>;
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? 'border-red-300 bg-red-50' : 'bg-white'}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-gray-400" />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

export default CommercialDashboard;
